import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Paper,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Check as CheckIcon, ShoppingCart as ShoppingCartIcon, Star as StarIcon } from '@mui/icons-material';
import api from '../../utils/axios';
import { loadStripe } from '@stripe/stripe-js';
import { Link as RouterLink } from 'react-router-dom';

// Initialize Stripe with environment variable
let stripePromise;
function getStripe() {
  if (!stripePromise) {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set. Stripe checkout will not work.');
      return null;
    }
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [hasDevice, setHasDevice] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        
        // Fetch current subscription first
        const subResponse = await api.get('/subscriptions/current');
        setCurrentSubscription(subResponse.data.subscription);
        
        // Then fetch available plans
        const plansResponse = await api.get('/subscriptions/plans');
        setPlans(plansResponse.data.plans);
        
        // Check if the user has any devices
        const deviceResponse = await api.get('/barix/devices');
        setHasDevice(deviceResponse.data.length > 0);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load subscription plans. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchPlans();
  }, []);
  
  const handleSubscribe = async (planId) => {
    // Atmosphere plan routes to contact sales — no Stripe checkout
    if (planId === 'atmosphere') {
      window.location.href = 'mailto:sales@setsync.io?subject=Atmosphere%20Plan%20Inquiry';
      return;
    }

    try {
      setCheckoutLoading(true);
      setError('');

      // Create Stripe checkout session
      const response = await api.post('/subscriptions/create-checkout-session', {
        planId,
      });

      // If the API returned a direct checkout URL, redirect to it
      // (Stripe Checkout hosted page — most reliable approach)
      if (response.data.url) {
        window.location.href = response.data.url;
        return;
      }

      // Fallback: use redirectToCheckout with sessionId
      const stripe = await getStripe();
      if (!stripe) {
        setError('Payment system is not configured. Please contact support.');
        setCheckoutLoading(false);
        return;
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId,
      });
      
      if (error) {
        setError(error.message);
      }
      
      setCheckoutLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    try {
      setCheckoutLoading(true);
      
      await api.post('/subscriptions/cancel');
      
      // Refresh subscription status
      const response = await api.get('/subscriptions/current');
      setCurrentSubscription(response.data.subscription);
      
      setCheckoutLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel subscription. Please try again.');
      setCheckoutLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  return (
    <Box sx={{ 
      bgcolor: 'background.default',
      minHeight: '100vh',
      py: 8
    }}>
      <Container maxWidth="lg">
        <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            fontWeight="700" 
            sx={{ 
              mb: 2,
              background: 'linear-gradient(90deg, #FFA500 0%, #00B2CC 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent'
            }}
          >
            Choose Your Plan
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, mb: 3 }}>
            1 credit = 1 hour of live DJ streaming. Scale with your schedule.
          </Typography>

          {/* Free trial badge */}
          <Chip
            label="✦ Your first event is free — no card required"
            color="warning"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.85rem', px: 1 }}
          />
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 4, maxWidth: 900, mx: 'auto' }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}
        
        {currentSubscription && (
          <Box mb={6} sx={{ maxWidth: 900, mx: 'auto' }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'primary.main',
                background: 'rgba(255, 165, 0, 0.05)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <Typography variant="h5" fontWeight="600" gutterBottom>
                Your Current Subscription
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Box 
                  sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: 'success.main',
                    mr: 1.5
                  }} 
                />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {currentSubscription.planName}
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                {currentSubscription.cancelAtPeriodEnd 
                  ? `Your subscription will end on ${new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}.`
                  : `Your subscription will automatically renew on ${new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}.`
                }
              </Typography>
              
              {!currentSubscription.cancelAtPeriodEnd && (
                <Button 
                  variant="outlined" 
                  color="error" 
                  sx={{ 
                    mt: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    py: 1.5
                  }}
                  onClick={handleCancelSubscription}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? <CircularProgress size={24} /> : 'Cancel Subscription'}
                </Button>
              )}
            </Paper>
          </Box>
        )}
        
        {!hasDevice && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              mb: 6, 
              borderRadius: 4,
              maxWidth: 900, 
              mx: 'auto',
              background: 'rgba(0, 178, 204, 0.05)',
              border: '1px solid rgba(0, 178, 204, 0.2)',
              backdropFilter: 'blur(8px)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <ShoppingCartIcon sx={{ mr: 3, fontSize: 32, color: 'primary.light' }} />
              <Box>
                <Typography variant="h5" fontWeight="600" gutterBottom>
                  Need a Device to Start Streaming?
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
                  To stream music to your venue, you'll need a Barix RetailPlayer M400 device ($399).
                  The Resident and Atmosphere plans include one free — or grab the Opener and order separately.
                </Typography>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/venue/dashboard"
                  sx={{ 
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 'none'
                  }}
                >
                  Order Device Now ($399)
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
        
        <Grid container spacing={isMobile ? 3 : 4} sx={{ maxWidth: 900, mx: 'auto' }} justifyContent="center">
          {plans.map((plan, index) => {
            const isAtmosphere = plan.id === 'atmosphere';
            const isHighlighted = index === 1; // The Resident

            return (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                    borderRadius: 4,
                    border: isHighlighted ? '2px solid' : '1px solid',
                    borderColor: isHighlighted ? 'primary.main' : 'divider',
                    boxShadow: isHighlighted ? '0 8px 24px rgba(0, 178, 204, 0.2)' : 'none',
                    bgcolor: 'background.paper',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isHighlighted 
                        ? '0 12px 30px rgba(0, 178, 204, 0.3)' 
                        : '0 8px 24px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  {isHighlighted && (
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        px: 2,
                        py: 0.75,
                        borderRadius: 6,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <StarIcon sx={{ fontSize: 14 }} />
                      Most Popular
                    </Box>
                  )}
                  
                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    {/* Plan name */}
                    <Typography 
                      variant="h5" 
                      component="h2" 
                      fontWeight="700" 
                      gutterBottom
                    >
                      {plan.name}
                    </Typography>

                    {/* Price */}
                    {plan.price ? (
                      <Typography 
                        variant="h4" 
                        component="div" 
                        fontWeight="700" 
                        color="primary.main"
                        sx={{ mb: 1 }}
                      >
                        {plan.price}
                      </Typography>
                    ) : (
                      <Typography 
                        variant="h5" 
                        component="div" 
                        fontWeight="700" 
                        color="primary.main"
                        sx={{ mb: 1 }}
                      >
                        Contact Sales
                      </Typography>
                    )}

                    {/* Hours callout */}
                    {plan.hoursLabel && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'warning.main',
                          fontWeight: 600,
                          mb: 1,
                          fontSize: '0.85rem',
                        }}
                      >
                        {plan.hoursLabel}
                      </Typography>
                    )}
                    
                    {plan.description && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          mb: 3,
                          lineHeight: 1.6
                        }}
                      >
                        {plan.description}
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <List sx={{ py: 0 }}>
                      {plan.features.map((feature, idx) => (
                        <ListItem key={idx} disableGutters sx={{ px: 0, py: 0.75 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon 
                              sx={{ 
                                color: isHighlighted ? 'primary.main' : 'text.primary',
                                fontSize: 20 
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature} 
                            primaryTypographyProps={{
                              fontSize: '0.95rem',
                              fontWeight: 400
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  
                  <CardActions sx={{ p: 4, pt: 0 }}>
                    {isAtmosphere ? (
                      <Button 
                        fullWidth 
                        variant="outlined"
                        href="mailto:sales@setsync.io?subject=Atmosphere%20Plan%20Inquiry"
                        sx={{ 
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          boxShadow: 'none'
                        }}
                      >
                        Contact Sales
                      </Button>
                    ) : (
                      <Button 
                        fullWidth 
                        variant={isHighlighted ? "contained" : "outlined"}
                        sx={{ 
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          boxShadow: 'none'
                        }}
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={checkoutLoading || (currentSubscription && !currentSubscription.cancelAtPeriodEnd)}
                      >
                        {checkoutLoading ? <CircularProgress size={24} /> : (isHighlighted ? 'Get Started' : 'Start Free Trial')}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ mt: 6, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {hasDevice 
              ? "You're all set! Your Barix device is ready to stream."
              : "The Resident plan includes a FREE Barix M400. Or grab The Opener and "
            }
            {!hasDevice && (
              <RouterLink 
                to="/venue/dashboard"
                style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
              >
                order hardware separately ($399)
              </RouterLink>
            )}
            {!hasDevice && "."}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SubscriptionPlans;
